// frontend/src/hooks/useSiteTypes.ts — the site-types read surface (list),
// mirroring useRoles. Project-scoped server state (FRONTEND-PATTERNS §State): the
// key is `['site-types', projectId]` so a mutation's invalidate refreshes the
// list. The list row carries every field (no detail endpoint — the edit drawer
// pre-fills from the row), so there is no `useSiteType` detail hook. The read is
// member-level (a vocabulary is named by every member; defining it stays
// `reference_data:manage`) and answers the project's included TIERS beside the
// types, each with its live free seats.
//
// 401 is the centralized apiClient's job (ADR-003) — errors bubble so the
// page's 5-state contract renders them.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SiteTypeListItem, SiteTypesListResponse, SiteTypeTier } from '@ems-app/schemas/site-types/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

export const siteTypesKeys = {
  /** List key — also the invalidation PREFIX. */
  all: (projectId: string) => ['site-types', projectId] as const,
};

/** The list read — the page's, and a form's Load the latest (a type has no detail read: the list row is its read). */
export async function fetchSiteTypes(projectId: string): Promise<SiteTypesListResponse> {
  const r = await apiClient.get<SiteTypesListResponse>(`/projects/${projectId}/site-types`);
  return r.data;
}

// Module-level selectors (referentially stable, §State select doctrine).
const selectItems = (r: SiteTypesListResponse): SiteTypeListItem[] => r.items;
const selectTiers = (r: SiteTypesListResponse): SiteTypeTier[] => r.tiers;

/** `enabled: false` OBSERVES the entry without asking: a form whose picker already holds the list (its own read, on
 *  the press) reads the tiers' seats beside it — nothing loads for a closed picker (ADR-080 §7). */
export function useSiteTypes(projectId: string, opts: { enabled?: boolean } = {}): UseQueryResult<SiteTypeListItem[], Error> {
  return useQuery({
    queryKey: siteTypesKeys.all(projectId),
    queryFn: () => fetchSiteTypes(projectId),
    enabled: projectId.length > 0 && (opts.enabled ?? true),
    select: selectItems,
  });
}

/** The tiers beside the list (the same read, one cache entry): the project's INCLUDED tiers and any a type still holds
 *  — named, the catalogue being data (ADR-055), each with its live free seats. */
export function useSiteTypeTiers(projectId: string, opts: { enabled?: boolean } = {}): UseQueryResult<SiteTypeTier[], Error> {
  return useQuery({
    queryKey: siteTypesKeys.all(projectId),
    queryFn: () => fetchSiteTypes(projectId),
    enabled: projectId.length > 0 && (opts.enabled ?? true),
    select: selectTiers,
  });
}

/** The tiers as a picker's source (ADR-080 §3 — the small kind, on the list's own key): the project's included tiers;
 *  one it no longer includes is an inactive row — never offered, shown marked where a type holds it. */
export function siteTypeTiersSource(projectId: string): SmallSource<SiteTypesListResponse> {
  return {
    kind: 'small',
    queryKey: siteTypesKeys.all(projectId),
    fetch: () => fetchSiteTypes(projectId),
    rows: (r) => r.tiers.map((t) => ({ id: t.code, label: t.name, pickable: true, inactive: !t.included, group: null })),
  };
}

/** The site types as a picker's source. A FIELD (the site form's type field · the designations form) lists every type
 *  under its TIER as a heading (`group` = the tier's code — the host names it with the seats, "Simple · 3 seats free",
 *  from the tiers it observes on the same entry), the tiers in the catalogue's order and the types in theirs under
 *  each; a FILTER (`byTier: false`) lists the types flat in their own order — the seats are the form's business. A
 *  disabled type is an inactive row (never offered to a field — shown marked where it is held). */
export function siteTypesSource(projectId: string, { byTier = true }: { byTier?: boolean } = {}): SmallSource<SiteTypesListResponse> {
  return {
    kind: 'small',
    queryKey: siteTypesKeys.all(projectId),
    fetch: () => fetchSiteTypes(projectId),
    rows: (r) => {
      if (!byTier) return r.items.map((ty) => ({ id: String(ty.id), label: ty.name, pickable: true, inactive: ty.is_disabled, group: null }));
      const order = new Map(r.tiers.map((t, i) => [t.code, i] as const));
      return [...r.items]
        .sort((a, b) => (order.get(a.license_tier_code) ?? r.tiers.length) - (order.get(b.license_tier_code) ?? r.tiers.length) || a.display_order - b.display_order)
        .map((ty) => ({ id: String(ty.id), label: ty.name, pickable: true, inactive: ty.is_disabled, group: ty.license_tier_code }));
    },
  };
}
